import React, { useEffect, useState } from "react";
import {
  Input,
  Dropdown,
  Checkbox,
  Button,
  Form,
  Segment,
  Icon,
  Label,
} from "semantic-ui-react";

/**
 * Filtros del listado v2. Disenados para coincidir 1:1 con los aceptados por
 * `userListV2Controller`:
 *   q, vertical, roles[], active, institutionId, partnerId, dateFrom,
 *   dateTo, orphans.
 *
 * El filtro `vertical` es un atajo de alto nivel: agrupa varios roles en
 * una sola opcion (Educa / REVI / Simulador / Otros). Se puede combinar con
 * `roles[]` para filtrar mas fino.
 *
 * Search con debounce de 350ms para no spamear al backend.
 */

const VERTICAL_OPTIONS = [
  {
    key: "all",
    value: "all",
    text: "Todas las verticales",
    icon: "globe",
  },
  {
    key: "educa",
    value: "educa",
    text: "Educa K12",
    icon: "graduation cap",
    label: { color: "yellow", empty: true, circular: true },
  },
  {
    key: "revi",
    value: "revi",
    text: "REVI",
    icon: "heartbeat",
    label: { color: "purple", empty: true, circular: true },
  },
  {
    key: "simulator",
    value: "simulator",
    text: "Simuladores VR",
    icon: "cubes",
    label: { color: "blue", empty: true, circular: true },
  },
  {
    key: "otros",
    value: "otros",
    text: "Otros (admin/user/client/free)",
    icon: "ellipsis horizontal",
    label: { color: "grey", empty: true, circular: true },
  },
];

const ROLE_OPTIONS = [
  { key: "admin", value: "admin", text: "Admin" },
  { key: "user", value: "user", text: "Usuario" },
  { key: "client", value: "client", text: "Cliente" },
  { key: "partner", value: "partner", text: "Partner" },
  { key: "partner-child", value: "partner-child", text: "Sub-partner" },
  { key: "teacher", value: "teacher", text: "Profesor" },
  { key: "student", value: "student", text: "Alumno" },
  { key: "institution", value: "institution", text: "Institucion" },
  {
    key: "institution-member",
    value: "institution-member",
    text: "Miembro inst.",
  },
  { key: "therapist", value: "therapist", text: "Terapeuta" },
  { key: "patient", value: "patient", text: "Paciente" },
  { key: "revi-admin", value: "revi-admin", text: "Admin REVI" },
  { key: "educaSimulator", value: "educaSimulator", text: "Simulador" },
  { key: "simulator-admin", value: "simulator-admin", text: "Admin Sim." },
  { key: "educaverse", value: "educaverse", text: "Educaverse" },
  { key: "revi", value: "revi", text: "REVI" },
  { key: "free", value: "free", text: "Free" },
];

const ACTIVE_OPTIONS = [
  { key: "all", value: "all", text: "Todos" },
  { key: "active", value: "true", text: "Activos" },
  { key: "inactive", value: "false", text: "Inactivos" },
];

export function UsersV2Filters({ filters, onChange, total }) {
  const [localQ, setLocalQ] = useState(filters.q || "");

  // Debounce 350ms para `q`
  useEffect(() => {
    const handle = setTimeout(() => {
      if (localQ !== (filters.q || "")) {
        onChange({ ...filters, q: localQ, page: 1 });
      }
    }, 350);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localQ]);

  const handleVerticalChange = (_, { value }) => {
    onChange({
      ...filters,
      vertical: value === "all" ? undefined : value,
      page: 1,
    });
  };

  const handleRolesChange = (_, { value }) => {
    onChange({ ...filters, roles: value, page: 1 });
  };

  const handleActiveChange = (_, { value }) => {
    let nextActive;
    if (value === "true") nextActive = true;
    else if (value === "false") nextActive = false;
    else nextActive = undefined;
    onChange({ ...filters, active: nextActive, page: 1 });
  };

  const handleOrphansToggle = (_, { checked }) => {
    onChange({ ...filters, orphans: checked, page: 1 });
  };

  const handleDateChange = (field) => (e) => {
    onChange({ ...filters, [field]: e.target.value || undefined, page: 1 });
  };

  const handleReset = () => {
    setLocalQ("");
    onChange({ page: 1, limit: filters.limit || 25 });
  };

  const activeValue =
    filters.active === true
      ? "true"
      : filters.active === false
        ? "false"
        : "all";

  const verticalValue = filters.vertical || "all";
  const hasActiveFilters =
    !!filters.q ||
    !!filters.vertical ||
    (filters.roles && filters.roles.length > 0) ||
    filters.active !== undefined ||
    !!filters.dateFrom ||
    !!filters.dateTo ||
    !!filters.orphans;

  return (
    <Segment style={{ marginBottom: "1em" }}>
      {/* Vertical destacado como acceso rapido */}
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <span style={{ fontWeight: 600, marginRight: 4 }}>Vertical:</span>
        {VERTICAL_OPTIONS.map((opt) => {
          const isActive = verticalValue === opt.value;
          return (
            <Button
              key={opt.key}
              type="button"
              size="small"
              compact
              color={isActive ? opt.label?.color || "blue" : undefined}
              basic={!isActive}
              onClick={() => handleVerticalChange(null, { value: opt.value })}
              icon
              labelPosition="left"
            >
              <Icon name={opt.icon} />
              {opt.text}
            </Button>
          );
        })}
      </div>

      <Form>
        <Form.Group widths="equal">
          <Form.Field>
            <label>Busqueda</label>
            <Input
              icon="search"
              placeholder="Email, nombre, apellido..."
              value={localQ}
              onChange={(_, { value }) => setLocalQ(value)}
            />
          </Form.Field>
          <Form.Field>
            <label>Roles especificos</label>
            <Dropdown
              placeholder="Filtrar por roles"
              fluid
              multiple
              selection
              search
              options={ROLE_OPTIONS}
              value={filters.roles || []}
              onChange={handleRolesChange}
            />
          </Form.Field>
          <Form.Field>
            <label>Estado</label>
            <Dropdown
              fluid
              selection
              options={ACTIVE_OPTIONS}
              value={activeValue}
              onChange={handleActiveChange}
            />
          </Form.Field>
        </Form.Group>
        <Form.Group widths="equal">
          <Form.Field>
            <label>Desde</label>
            <Input
              type="date"
              value={filters.dateFrom || ""}
              onChange={handleDateChange("dateFrom")}
            />
          </Form.Field>
          <Form.Field>
            <label>Hasta</label>
            <Input
              type="date"
              value={filters.dateTo || ""}
              onChange={handleDateChange("dateTo")}
            />
          </Form.Field>
          <Form.Field
            style={{ display: "flex", alignItems: "flex-end", gap: 12 }}
          >
            <Checkbox
              toggle
              label="Solo huerfanos"
              checked={!!filters.orphans}
              onChange={handleOrphansToggle}
            />
            <Button
              type="button"
              size="small"
              basic
              icon
              labelPosition="left"
              onClick={handleReset}
              disabled={!hasActiveFilters}
              style={{ marginLeft: "auto" }}
            >
              <Icon name="undo" />
              Limpiar
            </Button>
          </Form.Field>
        </Form.Group>
        {typeof total === "number" && (
          <div style={{ color: "#666", fontSize: 13 }}>
            <Label basic size="small">
              <Icon name="users" />
              {total.toLocaleString("es-ES")} resultado{total === 1 ? "" : "s"}
            </Label>
          </div>
        )}
      </Form>
    </Segment>
  );
}
